import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { utils, writeFile } from "xlsx-js-style";
import dayjs from "dayjs";
import { fetchState } from "@/comn/features/recoil";

import { useGridContext } from "./reducer";
import { FormControl } from "@/comn/components";
import { comnUtils } from "@/comn/utils";
import { useSetRecoilState } from "recoil";

const SHEET_REF: any = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
    "AA",
    "AB",
    "AC",
    "AD",
    "AE",
    "AF",
    "AG",
    "AH",
    "AI",
    "AJ",
    "AK",
    "AL",
    "AM",
    "AN",
    "AO",
    "AP",
    "AQ",
    "AR",
    "AS",
    "AT",
    "AU",
    "AV",
    "AW",
    "AX",
    "AY",
    "AZ",
    "BA",
    "BB",
    "BC",
    "BD",
];

export const GridTable = () => {
    const { _grid, headSchema, bodySchema, groupSchema, groupFootSchema } = useGridContext();

    const [exporting, setExporting] = useState(false);
    const _resolve = useRef<any>();
    const [sheet, setSheet] = useState<any>(null);
    const { t } = useTranslation();
    const regex = RegExp(/^\d{2}\/\d{2}\/\d{4}$/);
    const setFetch = useSetRecoilState(fetchState);

    const _ref = useRef<any>(null);
    const _interval = useRef<any>(null);

    // extension
    const _ext = useRef(".xlsx");

    const getCodeStatus = () => {
        if (!_ref.current) return;
        const dataLoading = _ref.current.querySelectorAll("[data-async='true'][data-status='loading']");
        const dataDone = _ref.current.querySelectorAll("[data-async='true'][data-status='done']");
        if (dataLoading.length <= 0) {
            makeExcel();
            clearInterval(_interval.current);
            setFetch((prev: any) => ({ ...prev, isLoading: false }));
        }
    };

    _grid.current._exportExcel = (arg?: any) => {
        const { excelName = "Export(" + dayjs().format("YYYY-MM-DD HH:mm:ss") + ")", sheetName = "Sheet" } = arg;
        if (_grid.current._exporting) return;

        if (arg.extension) {
            _ext.current = arg.extension;
        }

        _grid.current._exporting = true;
        _grid.current._exportExcelName = excelName;
        _grid.current._exportSheetName = sheetName;
        setExporting(true);
    };

    _grid.current._exportSheet = () => {
        _grid.current._sheet = true;
        setExporting(true);

        return new Promise((resolve, reject) => {
            _resolve.current = resolve;
        });
    };

    useEffect(() => {
        if (sheet !== null) {
            _resolve.current(sheet);
        }
    }, [sheet]);

    useEffect(() => {
        clearInterval(_interval.current);
        return () => {
            clearInterval(_interval.current);
        };
    }, []);

    const tableRef = useCallback((ref: any) => {
        if (!ref) return;

        _ref.current = ref;
        _interval.current = setInterval(getCodeStatus, 1000);

        setFetch((prev: any) => ({ ...prev, isLoading: true }));
    }, []);

    const makeExcel = () => {
        if (!_ref) return;

        const list = _ref.current.querySelectorAll("[hidden]");
        const trs = _ref.current.querySelectorAll("tr:has(th)");

        let widths = new Array(trs.length);
        let totalRows = _ref.current.querySelectorAll("tr").length;

        try {
            for (let i = 0; i < widths.length; i++) {
                widths[i] = new Array();
            }

            for (let i = 0; i < trs.length; i++) {
                const tr = trs[i];
                const ths = tr.querySelectorAll("th");

                let col = 0;

                for (let j = 0; j < 50; j++) {
                    if (widths[i][j] === null || widths[i][j] === undefined) {
                        col = j;
                        break;
                    }
                }

                for (let j = 0; j < ths.length; j++) {
                    const th = ths[j];
                    const w = th.getAttribute("data-width") || 100;
                    const colspan = th.getAttribute("colspan") || 1;
                    const rowspan = th.getAttribute("rowspan") || 1;

                    widths[i][col] = colspan === 1 ? w : Math.ceil(w / colspan);

                    if (rowspan > 1) {
                        for (let k = 1; k < rowspan; k++) {
                            widths[i + k][col] = w;
                        }
                    }

                    if (colspan > 1) {
                        for (let k = 1; k < colspan; k++) {
                            col++;
                            widths[i][col] = Math.ceil(w / colspan);
                        }
                    }
                    col++;
                }
            }
        } catch (e) {
            console.error(e);
        }

        for (let i = 0; i < list.length; i++) {
            list[i].innerHTML = "";
        }

        _ref.current.querySelectorAll("select").forEach((p: any) => {
            p.replaceWith(p.value === "" ? "" : p.options[p.selectedIndex].text);
        });

        _ref.current.querySelectorAll(".tooltip").forEach((p: any) => {
            p.replaceWith("");
        });

        const ws = utils.table_to_sheet(_ref.current, { raw: true });

        //ws["!cols"] = new Array(_grid.current._cols).fill({ width: 20 });
        ws["!cols"] = new Array(widths[widths.length - 1].length);

        for (let i = 0; i < ws["!cols"].length; i++) {
            ws["!cols"][i] = { width: widths[widths.length - 1][i] / 4 };
        }

        const splited = ws["!ref"]?.split(":");

        ws["!rows"] = [
            ...new Array(trs.length).fill({ hpt: 30 }),
            ...new Array(totalRows - trs.length).fill({ hpt: 20 }),
        ];

        //console.log(_ref.current.innerHTML);

        if (splited) {
            const [start, end] = splited;

            const startCol = start.match(/\D+/g)?.[0] as string;
            const endCol = end.match(/\D+/g)?.[0] as string;

            const startColIndex = SHEET_REF.findIndex((value: any) => value === startCol);
            const endColIndex = SHEET_REF.findIndex((value: any) => value === endCol);

            const startRow = start.match(/\d+/g)?.[0] as string;
            const endRow = end.match(/\d+/g)?.[0] as string;

            const gap = startColIndex;

            const colLength = endColIndex - startColIndex + 1;
            const rowLength = Number(endRow) - Number(startRow) + 1;

            const cellKeys = [];

            for (let i = 0; i < colLength; i++) {
                for (let j = 0; j < rowLength; j++) {
                    cellKeys.push(SHEET_REF[i + gap] + (j + 1));
                }
            }

            cellKeys.forEach((key) => {
                if (!ws[key]) ws[key] = { v: "" };
                ws[key].s = { border: {} };
                ws[key].s.border.top = { color: "000000", style: "thin" };
                ws[key].s.border.left = { color: "000000", style: "thin" };
                ws[key].s.border.top = { color: "000000", style: "thin" };
                ws[key].s.border.right = { color: "000000", style: "thin" };
                ws[key].s.border.bottom = { color: "000000", style: "thin" };
                ws[key].s.alignment = { wrapText: true };

                if (ws[key].t === "s") {
                    ws[key].s.alignment = { wrapText: true };
                    ws[key].s.alignment.vertical = "top";
                }

                if (ws[key].v?.startsWith?.("th:#:")) {
                    ws[key].s.alignment = { wrapText: true };
                    ws[key].v = ws[key].v.slice(5);
                    ws[key].s.font = { bold: true };
                    ws[key].s.fill = { fgColor: { rgb: "EBEFFA" } };
                    ws[key].s.alignment.vertical = "center";
                    ws[key].s.alignment.horizontal = "center";
                }

                if (ws[key].v?.startsWith?.("td:#:")) {
                    ws[key].t = ws[key].v.split(":#:")[1];

                    let z = "0";

                    // thousand separator
                    if (ws[key].t === "nz") {
                        ws[key].t = "n";
                        z = "#,##0";
                        ws[key].z = "#,##0";
                    }
                    ws[key].s.alignment.horizontal = ws[key].v.split(":#:")[2];
                    ws[key].v = ws[key].v.split(":#:")[3];
                    // number
                    if (ws[key].t === "n") {
                        ws[key].v = ws[key].v.replaceAll(",", "");

                        // decimal
                        if (ws[key].v.split(".")[1] !== undefined) {
                            z = z + ".";
                            for (let i = 0; i < ws[key].v.split(".")[1].length; i++) {
                                z += "0";
                            }
                            ws[key].z = z;
                        }
                    }
                }

                if (ws[key].v?.startsWith?.("tdg:#:")) {
                    ws[key].s.fill = { fgColor: { rgb: "EBFAEF" } };
                    ws[key].s.font = { bold: true };
                    ws[key].v = ws[key].v.split(":#:")[1];
                }

                if (ws[key].v?.startsWith?.("tdf:#:")) {
                    ws[key].s.fill = { fgColor: { rgb: "FAEBEF" } };
                    ws[key].s.font = { bold: true };
                    ws[key].v = ws[key].v.split(":#:")[1];
                }

                if (ws[key].t === "d" && ws[key].v === "") {
                    ws[key].t = "s";
                    ws[key].v = "";
                } else if (ws[key].t === "d") {
                    if (regex.test(ws[key].v) === true) {
                        let t = ws[key].v.split("/");
                        ws[key].v = `${t[1]}/${t[0]}/${t[2]}`;
                    }
                }

                if (ws[key].l) {
                    delete ws[key].l;
                }
            });
        }

        if (_grid.current._sheet === true) {
            setSheet(ws);
            _grid.current._sheet = false;
            setExporting(false);
        } else {
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, _grid.current._exportSheetName);
            writeFile(wb, _grid.current._exportExcelName + _ext.current);
            _grid.current._exporting = false;
            setExporting(false);
        }

        _ext.current = ".xlsx";
    };

    if (!exporting) return null;

    const checkWidth = (w: any) => {
        if (w === undefined || w === null) return 100;
        try {
            if (w.indexOf("*") > -1) return w.replace("*", "") == "" ? 100 : w.replace("*", "") * 100;
            if (w.indexOf("%") > -1) return w.replace("%", "") == "" ? 100 : w.replace("%", "") * 10;
        } catch (e) {
            //
        }
        return w;
    };

    return (
        <table ref={tableRef} className="hidden">
            <thead className="[&_th]:border">
                {headSchema.map((cols: any, rowIndex: any) => {
                    return (
                        <tr key={_grid.current._key + ".thr." + rowIndex}>
                            {cols.map((cel: any, celIndex: any) => {
                                if (!cel) return null;
                                return (
                                    <th
                                        key={_grid.current._key + ".th." + rowIndex + "." + celIndex}
                                        rowSpan={cel.rowspan}
                                        colSpan={cel.colspan}
                                        data-width={checkWidth(cel.width)}
                                    >
                                        th:#:{t(cel.header)}
                                    </th>
                                );
                            })}
                        </tr>
                    );
                })}
            </thead>
            <tbody>
                {_grid.current._viewAll?.map((row: any, rowIndex: any) => {
                    if (row.__type === "group") {
                        return groupSchema?.map((schemaRow: any, schemaIndex: any) => {
                            return (
                                <tr key={_grid.current._key + ".trg." + rowIndex + "." + schemaIndex}>
                                    {schemaRow.map((cel: any, celIndex: any) => {
                                        if (!cel) return null;
                                        if (cel.show === false) return null;
                                        const { binding, aggregate, align, text, decimalScale, thousandSeparator } =
                                            cel;

                                        const ag = row.aggregate.find((_: any) => {
                                            return _.binding === binding && _.aggregate === aggregate;
                                        });
                                        let value = ag?.value;

                                        if (typeof value === "number") {
                                            if (typeof decimalScale === "number") {
                                                value = value.toFixed(decimalScale);
                                            }
                                            if (thousandSeparator) {
                                                const x = String(value).split(".");
                                                const int = Number(x[0]).toLocaleString();
                                                const dec = x[1];
                                                value = int + (dec === undefined ? "" : "." + dec);
                                            }
                                        }

                                        return (
                                            <td
                                                key={_grid.current._key + ".tdg." + rowIndex + "." + celIndex}
                                                rowSpan={cel.rowspan}
                                                colSpan={cel.colspan}
                                            >
                                                tdg:#:{text ? t(text) : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        });
                    }

                    if (row.__type === "groupFoot") {
                        return groupFootSchema.map((schemaRow: any, schemaIndex: any) => {
                            return (
                                <tr key={_grid.current._key + ".trf." + rowIndex + "." + schemaIndex}>
                                    {schemaRow.map((cel: any, celIndex: any) => {
                                        if (!cel) return null;
                                        if (cel.show === false) return null;
                                        const { binding, aggregate, align, text, decimalScale, thousandSeparator } =
                                            cel;

                                        const ag = row.aggregate.find((_: any) => {
                                            return _.binding === binding && _.aggregate === aggregate;
                                        });
                                        let value = ag?.value;

                                        if (typeof value === "number") {
                                            if (typeof decimalScale === "number") {
                                                value = value.toFixed(decimalScale);
                                            }
                                            if (thousandSeparator) {
                                                const x = String(value).split(".");
                                                const int = Number(x[0]).toLocaleString();
                                                const dec = x[1];
                                                value = int + (dec === undefined ? "" : "." + dec);
                                            }
                                        }

                                        return (
                                            <td
                                                key={_grid.current._key + ".tdf." + rowIndex + "." + celIndex}
                                                rowSpan={cel.rowspan}
                                                colSpan={cel.colspan}
                                            >
                                                tdf:#:{text ? t(text) : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        });
                    }

                    return bodySchema.map((cols: any, colIndex: any) => {
                        return (
                            <tr key={_grid.current._key + ".tbr." + rowIndex + "." + colIndex}>
                                {cols.map((cel: any, celIndex: any) => {
                                    if (!cel) return null;

                                    const {
                                        binding,
                                        align,
                                        rowspan,
                                        colspan,
                                        edit,
                                        header,
                                        type,
                                        thousandSeparator = true,
                                        ...FORM
                                    } = cel;

                                    const BINDING_VAL = row[binding];
                                    const FORMATTED_VAL = comnUtils.getFormattedValue(BINDING_VAL, FORM);
                                    FORM.type = type;

                                    const CELL_CONTEXT = {
                                        binding,
                                        rowValues: row,
                                        value: BINDING_VAL,
                                        formattedValue: FORMATTED_VAL,
                                    };

                                    const BG_COLORS = {
                                        blue: "bg-[#bacee0]",
                                        yellow: "bg-[#ffeb33]",
                                        red: "bg-[#ed3e49]",
                                    };

                                    const TEXT_COLORS = {
                                        blue: "text-[#bacee0]",
                                        yellow: "text-[#ffeb33]",
                                        red: "text-[#ed3e49]",
                                    };

                                    switch (FORM.type) {
                                        case "daterange":
                                        case "timerange":
                                            {
                                                const START_VAL = row[FORM.start.binding];
                                                const END_VAL = row[FORM.end.binding];
                                                FORM.start.type = FORM.type === "daterange" ? "date" : "time";
                                                FORM.end.type = FORM.type === "daterange" ? "date" : "time";
                                                FORM.start.value = comnUtils.getFormattedValue(START_VAL, FORM.start);
                                                FORM.end.value = comnUtils.getFormattedValue(END_VAL, FORM.end);
                                            }
                                            break;
                                        default:
                                            {
                                                FORM.value = FORMATTED_VAL;
                                            }
                                            break;
                                    }
                                    FORM.edit = false;
                                    const Control = <FormControl {...FORM} />;

                                    let cellContext: any = {};

                                    //console.log(_grid.current._render["edit"]?.[binding]);
                                    //console.log(_grid.current._render["cell"]?.[binding]);
                                    //console.log(_grid.current._render["body"]?.[binding]);

                                    const CustomCell = _grid.current._render?.[
                                        edit ? edit : "cell" ? "cell" : "body"
                                    ]?.[binding]?.({ ...CELL_CONTEXT, control: Control }, cellContext);

                                    let t;

                                    switch (type) {
                                        case "number":
                                            if (thousandSeparator) {
                                                t = "nz";
                                            } else {
                                                t = "n";
                                            }
                                            break;
                                        case "date":
                                            t = "d";
                                            break;
                                        default:
                                            t = "s";
                                    }

                                    return (
                                        <td
                                            key={_grid.current._key + ".td." + rowIndex + "." + celIndex}
                                            rowSpan={cel.rowspan}
                                            colSpan={cel.colspan}
                                        >
                                            td:#:{t}:#:{cel.align || "center"}:#:{CustomCell || Control}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    });
                })}
            </tbody>
        </table>
    );
};
